import { useQuery } from '@tanstack/react-query';
import { axiosInstance } from '../../api/axiosConfig';
import { useAuthStore } from '@/Stores/auth-v2-store';

export type AvailableSocietyAction = 'contract' | 'deliveryProof' | 'presaleSheet' | 'warrantyPolicy';

export interface AvailableSociety {
  societyId: string;
  societyName: string;
  isPrimary: boolean;
  canCreateContract: boolean;
  deliveryProofCreate: number;
  presaleSheet: number;
  /** Porteur de la stratégie garantie ; NULL = la société porte la sienne. */
  warrantyPolicyOwnerId: string | null;
}

interface ApiSociety {
  societyId: string;
  societyName: string;
  isPrimary: boolean;
  assignedAt: Date;
  canCreateContract?: boolean;
  deliveryProofCreate?: number;
  presaleSheet?: number;
  warrantyPolicyOwnerId?: string | null;
}

const isEligible = (s: ApiSociety, action: AvailableSocietyAction): boolean => {
  switch (action) {
    case 'contract':
      return !!s.canCreateContract;
    case 'deliveryProof':
      return s.deliveryProofCreate === 2;
    case 'presaleSheet':
      return s.presaleSheet === 1;
    case 'warrantyPolicy':
      return true;
  }
};

const warrantyPolicyKey = (s: AvailableSociety): string => s.warrantyPolicyOwnerId ?? s.societyId;

/**
 * Une entrée par stratégie garantie distincte. La liste étant triée primaire
 * en tête, la société principale représente toujours sa propre stratégie.
 */
const uniqueByWarrantyPolicy = (societies: AvailableSociety[]): AvailableSociety[] => {
  const seen = new Set<string>();
  return societies.filter((s) => {
    const key = warrantyPolicyKey(s);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Liste les sociétés assignées à l'utilisateur courant et éligibles pour
 * l'action demandée (contract, deliveryProof, presaleSheet, warrantyPolicy).
 *
 * - Pour un external user : appelle GET /external-users-v2/:id/societies et
 *   filtre selon la config effective de chaque société.
 * - Pour un internal user ou un user non authentifié : renvoie un tableau vide
 *   et `isExternalUser: false` (les internals utilisent leur propre sélecteur).
 *
 * Les sociétés sont triées avec la primaire en tête.
 */
export const useAvailableSocieties = (action: AvailableSocietyAction) => {
  const currentUser = useAuthStore((s) => s.currentUser);
  const isExternalUser = currentUser?.type === 'EXTERNAL';
  const userId = currentUser?.id;

  const query = useQuery({
    queryKey: ['availableSocieties', userId, action],
    queryFn: async () => {
      const response = await axiosInstance.get<ApiSociety[]>(
        `/external-users-v2/${userId}/societies`,
      );
      const societies = response.data
        .filter((s) => isEligible(s, action))
        .map<AvailableSociety>((s) => ({
          societyId: s.societyId,
          societyName: s.societyName,
          isPrimary: s.isPrimary,
          canCreateContract: !!s.canCreateContract,
          deliveryProofCreate: s.deliveryProofCreate ?? 0,
          presaleSheet: s.presaleSheet ?? 0,
          warrantyPolicyOwnerId: s.warrantyPolicyOwnerId ?? null,
        }))
        .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
      return action === 'warrantyPolicy' ? uniqueByWarrantyPolicy(societies) : societies;
    },
    enabled: isExternalUser && !!userId,
    staleTime: 60_000,
  });

  return {
    societies: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    isExternalUser,
  };
};
